import { useState } from "react";
import { Plus, X, Wifi } from "lucide-react";
import { Modal, FormField, Button } from "../ui/index";
import { TYPES_CAPTEUR, TYPES_ACTIONNEUR } from "../../data/noeudTypes";

const typesByCategorie = {
  Capteur:    TYPES_CAPTEUR,
  Actionneur: TYPES_ACTIONNEUR,
  Passerelle: ["Gateway LoRa", "Gateway WiFi", "Gateway 4G"],
};

export default function AddNoeudModal({ isOpen, onClose, onAdd }) {
  const [sn, setSn] = useState("");
  const [categorie, setCategorie] = useState("");
  const [type, setType] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [mac, setMac] = useState("");
  const [localisation, setLocalisation] = useState("");
  const [fiche, setFiche] = useState("");
  const [error, setError] = useState("");

  // Calibration (capteurs uniquement)
  const [calMode, setCalMode] = useState("none");
  const [calMinRaw, setCalMinRaw] = useState("");
  const [calMaxRaw, setCalMaxRaw] = useState("");
  const [calMinPhysique, setCalMinPhysique] = useState("");
  const [calMaxPhysique, setCalMaxPhysique] = useState("");
  const [calUnitePhysique, setCalUnitePhysique] = useState("");

  const types = typesByCategorie[categorie] || [];

  const resetForm = () => {
    setSn(""); setCategorie(""); setType(""); setDeviceId(""); setMac("");
    setLocalisation(""); setFiche(""); setError("");
    setCalMode("none"); setCalMinRaw(""); setCalMaxRaw("");
    setCalMinPhysique(""); setCalMaxPhysique(""); setCalUnitePhysique("");
  };

  const handleSubmit = () => {
    setError("");
    if (!sn.trim()) { setError("Le numéro de série (SN) est obligatoire"); return; }
    if (!categorie) { setError("Sélectionnez une catégorie"); return; }
    if (!type) { setError("Sélectionnez un type d'appareil"); return; }

    let calibration = { mode: "none" };
    if (categorie === "Capteur" && calMode !== "none") {
      if (calMinPhysique === "" || calMaxPhysique === "" || !calUnitePhysique.trim()) {
        setError("Calibration : remplis min/max physique et l'unité physique");
        return;
      }
      if (calMode === "linear" && (calMinRaw === "" || calMaxRaw === "")) {
        setError("Calibration linéaire : remplis min/max brut");
        return;
      }
      calibration = {
        mode: calMode,
        minRaw: calMinRaw === "" ? null : parseFloat(calMinRaw),
        maxRaw: calMaxRaw === "" ? null : parseFloat(calMaxRaw),
        minPhysique: parseFloat(calMinPhysique),
        maxPhysique: parseFloat(calMaxPhysique),
        unitePhysique: calUnitePhysique.trim()
      };
    }

    onAdd({
      sn: sn.trim().toUpperCase(),
      categorie,
      type,
      deviceId: deviceId.trim() || undefined,
      mac: mac.trim() || undefined,
      localisation: localisation.trim() || undefined,
      fiche: fiche.trim() || undefined,
      calibration
    });
    resetForm();
    onClose();
  };

  const handleClose = () => { resetForm(); onClose(); };

  const selectStyle = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-700 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40";
  const inputStyle = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Ajouter un nœud au stock" subtitle="L'appareil sera en stock, prêt à être affecté via un chantier.">
      <div className="space-y-5">

        {/* SN */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <Wifi size={18} className="text-white" />
            </div>
            <div>
              <p className="font-semibold text-slate-800">Numéro de série (SN)</p>
              <p className="text-xs text-slate-500">Imprimé sur l'appareil physique ou sur son emballage</p>
            </div>
          </div>
          <input
            value={sn}
            onChange={e => setSn(e.target.value)}
            placeholder="Ex: CAPT-TEMP-001"
            className="w-full px-4 py-3 border-2 border-blue-300 rounded-xl text-sm font-mono font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
          />
          <p className="text-xs text-blue-600 mt-2">
            Ce SN sera utilisé pour identifier automatiquement l'appareil quand il se connectera au réseau
          </p>
        </div>

        {/* Type d'appareil */}
        <div>
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Type d'appareil</h3>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Catégorie" required>
              <select value={categorie} onChange={e => { setCategorie(e.target.value); setType(""); }} className={selectStyle}>
                <option value="">Sélectionner</option>
                <option>Capteur</option>
                <option>Actionneur</option>
                <option>Passerelle</option>
              </select>
            </FormField>
            <FormField label="Type" required>
              <select value={type} onChange={e => setType(e.target.value)} disabled={!categorie} className={selectStyle}>
                <option value="">Sélectionner</option>
                {types.map(t => <option key={t}>{t}</option>)}
              </select>
            </FormField>
          </div>
        </div>

        {/* Calibration (capteurs uniquement) */}
        {categorie === "Capteur" && (
          <div className="border border-gray-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-1">Calibration du signal</h3>
            <p className="text-xs text-slate-500 mb-3">Si le device envoie un signal brut (mA, V), Sagrada le convertit en valeur physique. Sinon laisse "Aucune".</p>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Mode">
                <select value={calMode} onChange={e => {
                  const m = e.target.value;
                  setCalMode(m);
                  if (m === "4-20mA") { setCalMinRaw("4"); setCalMaxRaw("20"); }
                  else if (m === "0-10V") { setCalMinRaw("0"); setCalMaxRaw("10"); }
                  else if (m === "none") { setCalMinRaw(""); setCalMaxRaw(""); }
                }} className={selectStyle}>
                  <option value="none">Aucune (valeur reçue utilisée telle quelle)</option>
                  <option value="4-20mA">4–20 mA (boucle de courant)</option>
                  <option value="0-10V">0–10 V (tension analogique)</option>
                  <option value="linear">Linéaire personnalisée</option>
                </select>
              </FormField>
              <FormField label="Unité physique">
                <input value={calUnitePhysique} onChange={e => setCalUnitePhysique(e.target.value)}
                  placeholder="°C, %, ppm, hPa…" className={inputStyle}
                  disabled={calMode === "none"} />
              </FormField>
            </div>
            {calMode !== "none" && (
              <div className="grid grid-cols-4 gap-3 mt-3">
                <FormField label="Min brut">
                  <input type="number" step="any" value={calMinRaw} onChange={e => setCalMinRaw(e.target.value)}
                    className={inputStyle} disabled={calMode === "4-20mA" || calMode === "0-10V"} />
                </FormField>
                <FormField label="Max brut">
                  <input type="number" step="any" value={calMaxRaw} onChange={e => setCalMaxRaw(e.target.value)}
                    className={inputStyle} disabled={calMode === "4-20mA" || calMode === "0-10V"} />
                </FormField>
                <FormField label="Min physique">
                  <input type="number" step="any" value={calMinPhysique} onChange={e => setCalMinPhysique(e.target.value)}
                    className={inputStyle} placeholder="0" />
                </FormField>
                <FormField label="Max physique">
                  <input type="number" step="any" value={calMaxPhysique} onChange={e => setCalMaxPhysique(e.target.value)}
                    className={inputStyle} placeholder="100" />
                </FormField>
              </div>
            )}
          </div>
        )}

        {/* Infos complémentaires */}
        <div>
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Informations complémentaires</h3>
          <div className="grid grid-cols-3 gap-3">
            <FormField label="Adresse MAC">
              <input value={mac} onChange={e => setMac(e.target.value)} placeholder="AA:BB:CC:DD:EE:FF" className={inputStyle} />
            </FormField>
            <FormField label="ID interne (alias)">
              <input value={deviceId} onChange={e => setDeviceId(e.target.value)} placeholder="Ex: TEMP-001" className={inputStyle} />
            </FormField>
            <FormField label="Localisation">
              <input value={localisation} onChange={e => setLocalisation(e.target.value)} placeholder="Ex: Bureau 1 - Étage 2" className={inputStyle} />
            </FormField>
          </div>
        </div>

        {/* Fiche technique */}
        <FormField label="Fiche technique (optionnel)">
          <textarea
            value={fiche}
            onChange={e => setFiche(e.target.value)}
            placeholder="URL de la fiche technique ou description du produit..."
            rows={2}
            className={`${inputStyle} resize-none`}
          />
        </FormField>

        {/* Erreur */}
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
            <X size={14} /> {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
          <Button variant="secondary" onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSubmit}><Plus size={16} /> Ajouter au stock</Button>
        </div>
      </div>
    </Modal>
  );
}
